import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

/**
 * Generic OAuth callback for the /onboarding/callback route.
 *
 * Handles two cases:
 *
 * 1. Google / GBP integrations (existing behaviour)
 *    - Shopify/Wix did NOT initiate this → no `code` query param
 *    - Expects `session_id` in the URL hash and `service` query param
 *    - POSTs to /integrations/google/callback
 *
 * 2. Shopify / Wix CMS OAuth (new)
 *    - `cms_oauth_platform` is present in sessionStorage (set before the redirect)
 *    - Shopify/Wix returns ?code=...&shop=...&hmac=...&timestamp=...
 *    - POSTs to /cms/shopify/callback or /cms/wix/callback
 *    - On success, navigates to /onboarding?cms_connected=<platform> so
 *      WebsiteAccessStep can detect it and mark the connection as done.
 */
export default function OnboardingCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      // ── Case 1: Shopify / Wix CMS OAuth ──────────────────────────────────
      const code = searchParams.get('code');
      const shop = searchParams.get('shop') || '';
      const hmac = searchParams.get('hmac') || '';
      const platform = sessionStorage.getItem('cms_oauth_platform') || (shop && hmac ? 'shopify' : '');

      if (code && platform) {
        const timestamp = searchParams.get('timestamp') || '';
        const state = searchParams.get('state') || '';
        const host = searchParams.get('host') || '';
        const shopifyParams = Object.fromEntries(searchParams.entries());
        const redirectUri = `${window.location.origin}/onboarding/callback`;

        sessionStorage.removeItem('cms_oauth_platform');

        try {
          if (platform === 'shopify') {
            await api.post('/cms/shopify/callback', {
              code,
              shop,
              redirect_uri: redirectUri,
              hmac,
              timestamp,
              state,
              host,
              shopify_params: shopifyParams,
            });
          } else if (platform === 'wix') {
            await api.post('/cms/wix/callback', { code });
          }

          const label = platform.charAt(0).toUpperCase() + platform.slice(1);
          toast.success(`${label} connected successfully!`);
          // Signal back to WebsiteAccessStep that connection succeeded
          navigate(`/onboarding?cms_connected=${platform}`);
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to connect CMS');
          navigate('/onboarding');
        }
        return;
      }

      // ── Case 2: Google / GBP integration ─────────────────────────────────
      const hash = window.location.hash;
      const sessionMatch = hash.match(/session_id=([^&]+)/);
      const sessionId = sessionMatch ? sessionMatch[1] : null;

      const service = searchParams.get('service');
      const from = searchParams.get('from');
      const returnPath = ['integrations', 'settings'].includes(from)
        ? '/dashboard/integrations'
        : '/onboarding';

      if (!sessionId) {
        toast.error('OAuth failed - no session ID received');
        navigate(returnPath);
        return;
      }

      if (!service) {
        toast.error('OAuth failed - no service specified');
        navigate(returnPath);
        return;
      }

      try {
        await api.post('/integrations/google/callback', {
          session_id: sessionId,
          service: service,
        });

        toast.success(`${service.toUpperCase()} connected successfully!`);

        window.history.replaceState(null, '', returnPath);
        navigate(returnPath);
      } catch (error) {
        console.error('OAuth callback error:', error);
        toast.error('Failed to connect service');
        navigate(returnPath);
      }
    };

    processCallback();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-indigo-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto mb-4"></div>
        <p className="text-slate-600">Connecting your account...</p>
      </div>
    </div>
  );
}
